import { useEffect, useMemo, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Star } from 'lucide-react';
import { mockData } from '../../data/mockData';
import { useToast } from '../../components/ui/use-toast';
import { useNavigate } from "react-router-dom";
import { useEvergreenCountdown } from "../../hooks/useEvergreenCountdown";

const NavratriReport = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [hideFooter, setHideFooter] = useState(false);

  useEffect(() => {
    const section = document.getElementById("pricing-section");
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setHideFooter(entry.isIntersecting); // true => hide, false => show
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  const { display } = useEvergreenCountdown("lifeReportOfferEnd", 4 * 60 * 60 * 1000);

    const handleScrollToPricing = () => {
    const element = document.getElementById('formSection');
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    gender: '',
    reportLanguage: '',
    dateOfBirth: '',
    timeOfBirth: '',
    placeOfBirth: '',
    whatsapp: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(mockData.plan[0]);
  const [finalAmount, setFinalAmount] = useState(
    parseInt(mockData.plan[0].priceFinal.replace("₹", ""))
  );
  console.log('selectedPlan.title', selectedPlan.title);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => setIsScriptLoaded(true);
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const createRazorpayOrder = async (amount, formData) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URLS_HTTP}api/life-journey-report/create-order`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ amount, formData }),
        }
      );
      if (!response.ok) throw new Error("Network response was not ok");
      return await response.json();
    } catch (error) {
      console.error("Error creating Razorpay order:", error);
      toast.error("Server Error: Could not create an order. Please try again later.");
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    const requiredFields = ['name', 'gender', 'dateOfBirth', 'timeOfBirth', 'placeOfBirth', 'whatsapp', 'email'];
    const missingFields = requiredFields.filter(field => !formData[field]?.trim());

    if (missingFields.length > 0) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!isScriptLoaded) {
      toast({
        variant: "destructive",
        title: "Payment gateway not ready",
        description: "Please wait a moment and try again.",
      });
      return;
    }
    setIsSubmitting(true);
    toast({ title: "Processing your request...", description: "Please wait." });
    const orderDetails = await createRazorpayOrder(finalAmount, {
      ...formData,
      planName: selectedPlan.title,
    });
    if (!orderDetails || !orderDetails.orderId) return;

    const razorpayKeyId =
      process.env.REACT_APP_RAZORPAY_SANDBOX === "true"
        ? process.env.REACT_APP_RAZORPAY_TEST_KEY_ID
        : process.env.REACT_APP_RAZORPAY_KEY_ID;

    const options = {
      key: razorpayKeyId,
      amount: finalAmount * 100,
      currency: "INR",
      name: "Astrology Consultation",
      description: selectedPlan.title,
      image: "/favicon.ico",
      order_id: orderDetails.orderId,
      handler: async function (response) {
        try {
          await fetch(
            `${process.env.REACT_APP_API_URLS_HTTP}api/life-journey-report/send-success-email`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                formData,
                razorpayResponse: response,
                planName: selectedPlan.title,
                dbOrderId: orderDetails.dbOrderId,
              }),
            }
          );
        } catch (err) {
          console.error("Failed to send success email:", err);
          setIsSubmitting(false);
        } finally {
          setIsSubmitting(false);
          const clientOrderId = `CN-${Date.now()}`;
          navigate("/order-confirmed", {
            state: {
              orderId: clientOrderId,
              plan: selectedPlan,
              finalAmount,
            },
          });
        }
      },
      prefill: {
        name: formData.name,
        email: formData.email,
        contact: formData.whatsapp,
      },
      theme: { color: "#391414" },
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', function (response) {
      // Optional: Ensure loader is off if they close the modal and fail
      setIsProcessingPayment(false);
      toast({ variant: "destructive", title: "Payment Failed", description: response.error.description || "Please try again." });
    });
    rzp.open();


  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-yellow-50">

      {/* Header Banner */}
      <div className="bg-gradient-to-r mt-[80px] from-orange-500 to-orange-600 text-white text-center py-3 px-4">
        <p className="text-sm md:text-base font-medium">
          Limited Time Diwali Special - Get 3 Reports for ₹1995
        </p>
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Left Sidebar - Product Images */}
          <div className="lg:col-span-2 space-y-4">
            {mockData.sidebarOffers.map((offer, index) => (
              <Card key={index} className="overflow-hidden border-2 border-orange-200 hover:border-orange-400 transition-all duration-300 transform hover:scale-105">
                <CardContent className="p-0">
                  <img
                    src={offer.image}
                    alt={offer.title}
                    className="w-full h-32 object-cover"
                  />
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-10">
            <div className="grid lg:grid-cols-12 gap-6">
              {/* Hero Section */}
              <div className="lg:col-span-7">
                <Card className="overflow-hidden border-4 border-red-600 shadow-2xl">
                  <CardContent className="p-0">
                    <div className="relative bg-gradient-to-br from-red-800 via-red-700 to-red-900">
                      <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent"></div>
                      <div className="relative grid grid-cols-12 items-start min-h-[500px]">
                        {/* Left Side - Text Content */}
                        <div className="col-span-7 p-8 text-white z-10">
                          <div className="bg-orange-600 text-white px-3 py-1 rounded-full text-xs font-bold inline-block mb-4">
                            Navratri OFFER!
                          </div>
                          <h1 className="text-3xl md:text-4xl font-bold mb-4">
                            <span className="text-yellow-300">Exclusive</span><br />
                            <span className="text-yellow-300">3-in-1 Report Package</span><br />
                            <span className="text-white text-5xl font-black">Limited Time Offer!</span>
                          </h1>

                          <p className="text-sm text-gray-200 mb-6">
                            Get guidance on <strong>Life, Love & Success</strong> in one package with Acharya Lavbhushan Ji’s powerful <strong>Reports & Remedies</strong>.
                          </p>
                          <p className="text-sm text-gray-200">✔ Numerology Life-Changing Report</p>
                          <p className="text-sm text-gray-200">✔ Astrology Life Journey Report</p>
                          <p className="text-sm text-gray-200">✔ Love Report</p>
                        </div>

                        {/* Right Side - Astrologer Image */}
                        <div className="col-span-5 flex justify-end items-center h-full">
                          <div className="relative">
                            <img
                              src={mockData.astrologerImage}
                              alt="Acharya Luvbhushan Ji"
                              className="w-64 h-80 object-cover rounded-l-3xl"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Bottom Products Row */}
                      {/* <div className="absolute bottom-4 left-8 flex space-x-3">
                        {mockData.heroProducts.map((product, index) => (
                          <img
                            key={index}
                            src={product.image}
                            alt={product.title}
                            className="w-16 h-20 object-cover rounded border-2 border-yellow-400"
                          />
                        ))}
                      </div> */}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right Form Section */}
              <div className="lg:col-span-5">
                <Card id="formSection" className="border-2 border-orange-300 shadow-xl">
                  <CardContent className="p-6">
                    <div className="mb-6">
                      <h2 className="text-2xl font-bold text-gray-800 mb-2">
                        3 Premium Reports
                      </h2>
                      <div className="flex items-center gap-2 mb-4">
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star key={star} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          ))}
                        </div>
                        <span className="text-sm text-gray-600">(200+ Reviews)</span>
                      </div>
                      <div className="flex items-center gap-4">
                        Value: <span className="text-sm text-gray-500 line-through">₹4999/-</span>
                        <span className="text-xl font-bold text-red-600"> → Now Only ₹1995/-</span>
                      </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1 block">
                            Name *
                          </label>
                          <Input
                            type="text"
                            placeholder="Enter your name"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            required
                            className="border-orange-200 focus:border-orange-400"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1 block">
                            Report Language *
                          </label>
                          <Select onValueChange={(value) => handleInputChange('reportLanguage', value)}>
                            <SelectTrigger className="border-orange-200 focus:border-orange-400">
                              <SelectValue placeholder="Report Language" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="english">English</SelectItem>
                              <SelectItem value="hindi">Hindi</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1 block">
                            Gender *
                          </label>
                          <Select onValueChange={(value) => handleInputChange('gender', value)}>
                            <SelectTrigger className="border-orange-200 focus:border-orange-400">
                              <SelectValue placeholder="Select Gender" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="male">Male</SelectItem>
                              <SelectItem value="female">Female</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1 block">
                            Date of Birth *
                          </label>
                          <Input
                            type="date"
                            value={formData.dateOfBirth}
                            onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                            required
                            className="border-orange-200 focus:border-orange-400"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1 block">
                            Time of Birth *
                          </label>
                          <Input
                            type="time"
                            value={formData.timeOfBirth}
                            onChange={(e) => handleInputChange('timeOfBirth', e.target.value)}
                            required
                            className="border-orange-200 focus:border-orange-400"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1 block">
                            Place of Birth *
                          </label>
                          <Input
                            type="text"
                            placeholder="Enter birth place (City, State)"
                            value={formData.placeOfBirth}
                            onChange={(e) => handleInputChange('placeOfBirth', e.target.value)}
                            required
                            className="border-orange-200 focus:border-orange-400"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1 block">
                            Whatsapp *
                          </label>
                          <Input
                            type="tel"
                            placeholder="Enter your WhatsApp number"
                            value={formData.whatsapp}
                            onChange={(e) => handleInputChange('whatsapp', e.target.value)}
                            required
                            className="border-orange-200 focus:border-orange-400"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1 block">
                            Email *
                          </label>
                          <Input
                            type="email"
                            placeholder="Enter your email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            required
                            className="border-orange-200 focus:border-orange-400"
                          />
                        </div>
                      </div>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-3 text-lg rounded-full transition-all duration-300 transform hover:scale-105"
                      >
                        Get All Reports @ <span className='line-through'>₹4999</span>{isSubmitting ? 'Submitting...' : ' → ₹1995'}
                      </Button>

                      <p className="text-xs text-gray-500 text-center">
                        100% privacy & secure delivery within 3 days
                      </p>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 mb-12">
          {mockData.stats.map((stat, index) => (
            <div key={index} className="text-center bg-white rounded-xl p-6 shadow-lg">
              <div className="text-3xl font-bold text-orange-600 mb-2">{stat.rating}</div>
              <div className="text-sm text-gray-600 uppercase tracking-wide">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* What Will You Get Section */}
        <Card className="mb-12 border-2 border-orange-200 shadow-xl">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">What Will You Get in This 3-in-1 Premium Package?</h2>
              <div className="flex justify-center items-center gap-4 mb-6">
                <span className="text-lg text-gray-500 line-through">Value: ₹4999/-</span>
                <span className="text-2xl font-bold text-red-600">Diwali Price: Just ₹1995/-</span>
              </div>
              <p className="text-gray-600 mb-6">
                One decision, complete clarity. Unlock guidance for your life, destiny & relationships with Acharya Lavbhushan Ji’s exclusive reports.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {mockData.offerings.map((offer, index) => (
                <div
                  key={index}
                  className="flex gap-4 bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg"
                >
                  {/* Left Icon */}
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>

                  {/* Right Content */}
                  <div className="flex flex-col">
                    <p className="font-semibold text-gray-800">{offer.title}</p>
                    <p className="text-sm text-gray-600">{offer.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-1 gap-6 text-center">
              <Badge variant="destructive" className="text-lg py-2 max-w-4xl mx-auto px-4 bg-red-600" onClick={() => document.getElementById('formSection').scrollIntoView({ behavior: 'smooth' })}>
                Get All 3 Reports for Just Value &nbsp;<span className='line-through'> ₹4999</span> → Now Only ₹1995/-
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Astrologer Section */}
        <Card className="mb-12 border-2 border-orange-200 shadow-xl">
          <CardContent className="p-8">
            <div className="grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-4 text-center">
                <img
                  src={mockData.astrologerProfile.image}
                  alt="Acharya Luvbhushan Ji"
                  className="w-48 h-64 object-cover rounded-2xl mx-auto mb-4 shadow-lg"
                />
                <Badge className="bg-green-600 text-white px-4 py-1">
                  Meet India's No.1 Astrologer
                </Badge>
              </div>
              <div className="md:col-span-8">
                <h3 className="text-3xl font-bold text-gray-800 mb-4">Acharya Luvbhushan ji</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {mockData.astrologerProfile.description}
                </p>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {mockData.astrologerProfile.description2}
                </p>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {mockData.astrologerProfile.description3}
                </p>
                <div className="text-center mb-6">
                  <Badge className="bg-orange-600 text-white text-lg px-6 py-2">
                    Personalized Predictions. Powerful Remedies.
                  </Badge>
                </div>
                <img
                  src={mockData.featuredLogos}
                  alt="Featured In"
                  className="w-full max-w-md mx-auto"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Testimonials Section */}
        <Card className="mb-12 border-2 border-orange-200 shadow-xl">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">What Others Are Saying</h2>
              <p className="text-gray-600">
                Real stories from people who discovered clarity, hope, and direction with Acharya Lavbhushan Ji’s guidance.
              </p>
            </div>

            <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
              {mockData.testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl shadow-md hover:shadow-xl transition-all p-6 flex flex-col"
                >
                  {/* Stars */}
                  <div className="flex mb-3">
                    {Array(5).fill(0).map((_, i) => (
                      <svg
                        key={i}
                        className="w-5 h-5 text-yellow-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.174c.969 0 1.371 1.24.588 1.81l-3.378 2.455a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.54 1.118l-3.379-2.454a1 1 0 00-1.176 0l-3.378 2.454c-.785.57-1.84-.196-1.54-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.05 9.394c-.783-.57-.38-1.81.588-1.81h4.174a1 1 0 00.95-.69l1.287-3.967z" />
                      </svg>
                    ))}
                  </div>
                  {/* Author */}
                  <p className="font-semibold text-gray-900">
                    – {testimonial.name}, {testimonial.location}
                  </p>
                  {/* Feedback */}
                  <p className="text-gray-700 italic leading-relaxed mb-4">
                    “{testimonial.feedback}”
                  </p>


                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Final CTA */}
        
        {/* Countdown Timer */}
        <div className={`fixed inset-x-0 bottom-0 z-50  bg-gradient-to-b from-cosmic-gold to-cosmic-glow border-t border-cosmic-gold/40 shadow-2xl pb-[env(safe-area-inset-bottom)] transition-transform duration-300 md:block ${hideFooter ? "translate-y-full" : "translate-y-0"
          } md:translate-y-0`}>
          <div className="mx-auto w-full max-w-screen-xl px-3 sm:px-6 py-2 sm:py-3">
            <div className="flex flex-col sm:flex-row items-center sm:justify-center gap-2 sm:gap-6">

              {/* Price (show only final price on mobile) */}
              <div className="text-primary-foreground text-center sm:text-left leading-none">
                <span className="inline-block text-sm sm:text-base line-through opacity-80 px-2">₹4999</span>
                <span className="inline-block text-xl sm:text-3xl font-bold sm:ml-2">₹1995</span>
              </div>

              {/* Button */}
              <Button
                onClick={handleScrollToPricing}
                variant="secondary"
                className="w-full sm:w-auto h-10 sm:h-12 px-4 sm:px-6 text-sm sm:text-base bg-red-600 text-destructive-foreground "
              >
                GET YOUR REPORT NOW
              </Button>

              <div className="text-primary-foreground text-center sm:text-left leading-none">
                <span className="text-sm">Offer ends soon in:</span>
                <div className="text-base sm:text-2xl font-bold tabular-nums">{display}</div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavratriReport;